import { Injectable, NgZone } from '@angular/core';
import { ShellQuery } from "@shared/store/shell/shell-query";
import { loadRemoteModule } from '@angular-architects/module-federation';
import { IteroRemoteApplication } from "@shared/models/itero-remote-application";
import { RenderOptions } from "@shared/models/render.options";
import { filter, firstValueFrom } from "rxjs";
import { LoggerService } from "@core/services/logger/logger.service";
import { map } from "rxjs/operators";

@Injectable({
	providedIn: 'root'
})
export class MfeLoaderService {
	private remoteApplicationLoaderPromise?: Promise<IteroRemoteApplication>;
	private readonly moduleName = 'MfeLoaderService';

	constructor(private readonly shellQuery: ShellQuery,
				private readonly ngZone: NgZone,
				private readonly logger: LoggerService) {
	}

	async renderRemote(renderOptions: RenderOptions): Promise<boolean> {
		try {
			const remoteApplicationLoader = await this.initialize();
			if (remoteApplicationLoader) {
				await remoteApplicationLoader.renderRemote(renderOptions);
				return true;
			}
			return false;

		} catch (err) {
			this.logger.error(`Error during renderRemote`, {
				module: this.moduleName,
				error: err
			});
			return false;
		}
	}

	private async initialize(): Promise<IteroRemoteApplication | undefined> {
		if (!this.remoteApplicationLoaderPromise) {
			let ralUrl = await this.getRalUrl();

			if (!ralUrl) {
				return undefined;
			}
			ralUrl = ralUrl.replace(/\/mf-manifest\.json$/, '');
			const ral = await this.loadRal(ralUrl);

			await ral.initRemotes({ ngZone: this.ngZone });

			this.remoteApplicationLoaderPromise = Promise.resolve(ral);
		}
		return this.remoteApplicationLoaderPromise;
	}

	private async loadRal(ralUrl: string) {
		return await loadRemoteModule({
			remoteName: 'remoteApplication',
			type: 'script',
			exposedModule: '.',
			remoteEntry: `${ralUrl}/remoteEntry.js`
		});
	}

	private async getRalUrl() {
		return await firstValueFrom(
			this.shellQuery.appSettings$.pipe(
				filter(x => x.remoteApplicationLoaderUrl?.length > 0),
				map((x) => x.remoteApplicationLoaderUrl)));
	}
}